import React, { useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { Button } from '@opendate-app/setlist';
import CrmPartyAutocomplete from '../../../shared/components/CrmPartyAutocomplete';
import crmLabels from '../../../shared/utils/crmLabels';

const contactShape = PropTypes.shape({
  id: PropTypes.number.isRequired,
  name: PropTypes.string,
});

function IntroducedByCard({ team, contactId, csrfToken, introducedBy, introducedContacts, canEdit }) {
  const labels = crmLabels(team);
  const [introducer, setIntroducer] = useState(introducedBy);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const contactHref = (id) => `/teams/${team.id}/crm/fans/${id}`;
  const { count, contacts } = introducedContacts;

  const save = async (next) => {
    setSaving(true);
    setError('');
    try {
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
      await axios.patch(`/teams/${team.id}/crm/fans/${contactId}.json`, {
        fan: { introduced_by_id: next ? next.id : null },
      });
      setIntroducer(next);
    } catch (err) {
      const messages = err.response && Array.isArray(err.response.data) ? err.response.data : [];
      setError(messages.join(', ') || 'Could not save who introduced this contact');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cdp-card" data-testid="introduced-by-card">
      <h2 className="cdp-card-title">Introduced by</h2>

      {introducer && (
        <div className="d-flex align-items-center justify-content-between">
          <a href={contactHref(introducer.id)} className="cdp-affiliation-name" data-testid="introduced-by-link">
            {introducer.name || '(No name)'}
          </a>
          {canEdit && (
            <Button variant="link-gray" size="sm" onClick={() => save(null)} disabled={saving} aria-label="Remove introducer">
              Remove
            </Button>
          )}
        </div>
      )}
      {!introducer && canEdit && saving && <div className="text-muted small" data-testid="introduced-by-saving">Saving…</div>}
      {!introducer && canEdit && !saving && (
        <CrmPartyAutocomplete
          team={team}
          fanOnly
          omitFanIds={[contactId]}
          value={null}
          onChange={(picked) => { if (picked && picked.type === 'fan') save(picked); }}
          placeholder={`Search ${labels.contact.plural.toLowerCase()}…`}
          testId="introduced-by-picker"
        />
      )}
      {!introducer && !canEdit && <div className="text-muted small">No one recorded</div>}
      {error && <div className="text-danger small mt-1">{error}</div>}

      {count > 0 && (
        <div className="cdp-affiliation-row mt-2" data-testid="introduced-contacts">
          <div>
            <div className="cdp-card-eyebrow">{`Introduced ${count} ${count === 1 ? labels.contact.singular.toLowerCase() : labels.contact.plural.toLowerCase()}`}</div>
            {contacts.map((introduced) => (
              <div key={introduced.id}>
                <a href={contactHref(introduced.id)} className="cdp-affiliation-name">{introduced.name || '(No name)'}</a>
              </div>
            ))}
            {count > contacts.length && <div className="text-muted small">{`and ${count - contacts.length} more`}</div>}
          </div>
        </div>
      )}
    </div>
  );
}

IntroducedByCard.propTypes = {
  team: PropTypes.shape({ id: PropTypes.number.isRequired }).isRequired,
  contactId: PropTypes.number.isRequired,
  csrfToken: PropTypes.string.isRequired,
  introducedBy: contactShape,
  introducedContacts: PropTypes.shape({
    count: PropTypes.number.isRequired,
    contacts: PropTypes.arrayOf(contactShape).isRequired,
  }),
  canEdit: PropTypes.bool,
};

IntroducedByCard.defaultProps = {
  introducedBy: null,
  introducedContacts: { count: 0, contacts: [] },
  canEdit: false,
};

export default IntroducedByCard;
